import { recoveryHasNoEdits, recoveryIsSuperseded, recoveryRecordId, recoveryType } from './editorRecoveryComparison';
import { encryptClinicalPayload as encryptPayload, decryptClinicalPayload as decryptPayload } from './clinicalEncryption';
import { getOfflineOwnerIdentity } from './offlineIdentity';
import { getVaultStatus } from './vault';
import { setPreparedChartPin, listPreparedCharts, registerPreparedRetentionReconciler } from './preparedChartStore';
import { getOfflineOwnerKey } from './chartDraftStore';
import { compareAndSwapRecord, listStoredRecords, newOperationId, readStoredRecord, storageKey } from './durableStore';

export { recoveryMatchesServer } from './editorRecoveryComparison';

const changed = () => window.dispatchEvent(new CustomEvent('vela:editor-recovery-changed'));
const assertOwner = owner => {
  if (owner !== getOfflineOwnerKey()) throw new Error('Practice access changed. Reopen the chart before saving.');
};

// Each page instance owns a branch. Tabs never replace one another's complete
// editor snapshots. A reload can explicitly restore the newest retained branch.
const branches = new Map();
const branchFor = (owner, logicalKey) => {
  const mapKey = JSON.stringify([owner, logicalKey]);
  if (!branches.has(mapKey)) branches.set(mapKey, { id: newOperationId(), source: null });
  return branches.get(mapKey);
};
const decodeRecords = async owner => Promise.all((await listStoredRecords(owner, 'editor-recovery')).map(async stored => {
  try { return { stored, value: await decryptPayload(stored.payload) }; }
  catch { return { stored, value: null }; }
}));
// Older labor editors shared one key across every record in an episode.
// Address those copies by their own record without rewriting clinical content.
const logicalKey = value => value?.chartType === 'labor-record' && /^labor-record-editor:[^:]+$/.test(value.key)
  ? `${value.key}:${recoveryRecordId(value) || 'new'}` : value?.key;
const expose = ({ stored, value }) => ({ ...value, key: logicalKey(value), recoveryId: stored.key, storageRevision: stored.storageRevision });
const pinClient = async (owner, clientReferenceId, pinned = true) => {
  if (!clientReferenceId) return;
  assertOwner(owner);
  await setPreparedChartPin({ clientReferenceId }, 'editor-recovery', pinned);
  assertOwner(owner);
};
const withRetentionLock = async (owner, clientReferenceId, action) => {
  if (clientReferenceId && navigator.locks?.request) {
    const lockKey = await storageKey(owner, 'editor-recovery-retention', clientReferenceId);
    return navigator.locks.request(lockKey, { mode: 'exclusive' }, () => action(true));
  }
  // Without a cross-tab lock, an empty scan cannot prove that another tab is
  // not between pinning and committing. Keep an extra pin rather than evict
  // dependencies belonging to a new editor snapshot.
  return action(false);
};
export async function reconcileEditorRecoveryPins(ownerIdentity = null) {
  // Logout changes the active identity before asynchronous cleanup finishes.
  // Captured-owner reconciliation is add-only and never opens clinical data.
  if (ownerIdentity && (getOfflineOwnerIdentity()?.ownerKey !== ownerIdentity.ownerKey
    || !(await getVaultStatus(ownerIdentity)).unlocked)) {
    const capturedOwner = `${ownerIdentity.contextReferenceId}:${ownerIdentity.userReferenceId}`;
    const records = await listStoredRecords(capturedOwner, 'editor-recovery');
    for (const clientReferenceId of new Set(records.map(record => record.clientReferenceId).filter(Boolean))) {
      await setPreparedChartPin({ clientReferenceId }, 'editor-recovery', true, ownerIdentity);
    }
    if (records.some(record => !record.clientReferenceId)) {
      throw new Error('Unlock and review retained device recovery copies before clearing prepared charts.');
    }
    return;
  }
  const owner = getOfflineOwnerKey();
  const entries = await decodeRecords(owner);
  const manifests = entries.some(item => !(item.value?.clientReferenceId || item.stored.clientReferenceId))
    ? await listPreparedCharts() : [];
  let unknown = false;
  const clients = new Set();
  for (const item of entries) {
    const clientReferenceId = item.value?.clientReferenceId || item.stored.clientReferenceId
      || manifests.find(chart => item.value?.pregnancyReferenceId && chart.pregnancyReferenceId
        && String(chart.pregnancyReferenceId) === String(item.value.pregnancyReferenceId))?.clientReferenceId;
    if (clientReferenceId) clients.add(clientReferenceId);
    else unknown = true;
  }
  for (const clientReferenceId of clients) await pinClient(owner, clientReferenceId);
  if (unknown) throw new Error('Unlock and review retained device recovery copies before clearing prepared charts.');
}

// Recovery snapshots are complete editor states, not HTTP requests. They are
// never candidates for automatic replay, even after a manual "Retry sync".
export async function saveEditorRecovery(input) {
  if (!input.key || input.state === undefined) throw new Error('A recovery identity and editor state are required.');
  const owner = getOfflineOwnerKey();
  const branch = branchFor(owner, input.key);
  const key = await storageKey(owner, 'editor-recovery', [input.key, branch.id]);
  const capturedAt = new Date().toISOString();
  return withRetentionLock(owner, input.clientReferenceId, async () => {
    await pinClient(owner, input.clientReferenceId);
    for (let attempt = 0; attempt < 8; attempt += 1) {
      const stored = await readStoredRecord(key);
      const previous = stored ? await decryptPayload(stored.payload) : null;
      const record = {
        ...input, branchId: branch.id,
        snapshotRevision: newOperationId(),
        operationId: input.operationId || previous?.operationId || newOperationId(),
        base: previous?.operationId === input.operationId ? previous?.base ?? input.base ?? null : input.base ?? null,
        capturedAt,
        createdAt: previous?.createdAt || capturedAt,
        recoveryOnly: true,
      };
      delete record.recoveryId;
      delete record.storageRevision;
      const payload = await encryptPayload(record);
      assertOwner(owner);
      if (await compareAndSwapRecord(key, stored?.storageRevision || 0, { owner, kind: 'editor-recovery', clientReferenceId: input.clientReferenceId, payload })) {
        // Restoring a former tab transfers its exact unchanged copy only after
        // this page's complete replacement has committed durably.
        const source = branch.source;
        if (source && source.value.operationId === record.operationId) {
          assertOwner(owner);
          await compareAndSwapRecord(source.stored.key, source.stored.storageRevision, null);
          branch.source = null;
        }
        await pinClient(owner, input.clientReferenceId);
        changed();
        return { ...record, recoveryId: key };
      }
    }
    throw new Error('Another editor is saving this chart. Keep the chart open and try again.');
  });
}

export async function readEditorRecovery(identity, recoveryId = null, serverContext = null) {
  if (serverContext) await reconcileEditorSection({ ...serverContext, key: identity });
  const owner = getOfflineOwnerKey();
  let branch = branchFor(owner, identity);
  if (recoveryId) {
    const selected = (await decodeRecords(owner)).find(item => item.stored.key === recoveryId && logicalKey(item.value) === identity);
    assertOwner(owner);
    if (!selected) return null;
    branch = { id: newOperationId(), source: selected };
    branches.set(JSON.stringify([owner, identity]), branch);
    await pinClient(owner, selected.value.clientReferenceId || selected.stored.clientReferenceId);
    return expose(selected);
  }
  const ownKey = await storageKey(owner, 'editor-recovery', [identity, branch.id]);
  const own = await readStoredRecord(ownKey);
  if (own && own.owner === owner) {
    const value = await decryptPayload(own.payload);
    assertOwner(owner);
    await pinClient(owner, value.clientReferenceId || own.clientReferenceId);
    return expose({ stored: own, value });
  }
  const candidates = (await decodeRecords(owner)).filter(item => logicalKey(item.value) === identity)
    .sort((left, right) => Date.parse(right.value.capturedAt) - Date.parse(left.value.capturedAt));
  assertOwner(owner);
  branch.source = candidates[0] || null;
  if (branch.source) await pinClient(owner, branch.source.value.clientReferenceId || branch.source.stored.clientReferenceId);
  return branch.source ? expose(branch.source) : null;
}

export async function listEditorRecoveries() {
  const owner = getOfflineOwnerKey();
  const entries = await decodeRecords(owner);
  for (const clientReferenceId of new Set(entries.map(item => item.value?.clientReferenceId || item.stored.clientReferenceId).filter(Boolean))) await pinClient(owner, clientReferenceId);
  const result = entries.map(item => item.value ? expose(item)
    : { key: item.stored.key, recoveryId: item.stored.key, unreadable: true, recoveryOnly: true });
  assertOwner(owner);
  return result;
}

export async function removeEditorRecovery(identity, operationId = null, snapshotRevision = null, recoveryId = null, storageRevision = null) {
  const owner = getOfflineOwnerKey();
  const branch = branchFor(owner, identity);
  const ownKey = await storageKey(owner, 'editor-recovery', [identity, branch.id]);
  const candidates = (await decodeRecords(owner)).filter(({ stored, value }) => logicalKey(value) === identity
    && (!operationId || value.operationId === operationId)
    && (storageRevision == null || stored.storageRevision === storageRevision)
    && (recoveryId ? stored.key === recoveryId && (!snapshotRevision || value.snapshotRevision === snapshotRevision)
      : snapshotRevision ? value.snapshotRevision === snapshotRevision : stored.key === ownKey));
  // A snapshot revision identifies one captured state in one branch. Without
  // one, cleanup can only touch this page's own branch, never another tab.
  if (candidates.length !== 1) return false;
  const { stored, value } = candidates[0];
  const clientReferenceId = value.clientReferenceId || stored.clientReferenceId;
  return withRetentionLock(owner, clientReferenceId, async canUnpin => {
    assertOwner(owner);
    const removed = await compareAndSwapRecord(stored.key, stored.storageRevision, null);
    if (removed) {
      if (branch.source?.stored.key === stored.key) branch.source = null;
      const retained = await decodeRecords(owner);
      const hasClientCopy = retained.some(item => (item.value?.clientReferenceId || item.stored.clientReferenceId) === clientReferenceId);
      const hasUnknownCopy = retained.some(item => !item.value && !item.stored.clientReferenceId);
      if (hasClientCopy) await pinClient(owner, clientReferenceId);
      else if (canUnpin && !hasUnknownCopy) await pinClient(owner, clientReferenceId, false);
      changed();
    }
    return removed;
  });
}

// Explicit discard removes all retained branches of this logical section.
// Receipt cleanup continues to remove only its exact submitted snapshot.
export async function discardEditorRecoveries({ key, clientReferenceId }) {
  const owner = getOfflineOwnerKey();
  return withRetentionLock(owner, clientReferenceId, async canUnpin => {
    const candidates = (await decodeRecords(owner)).filter(item => logicalKey(item.value) === key
      && item.value?.clientReferenceId === clientReferenceId);
    for (const { stored } of candidates) {
      assertOwner(owner);
      if (!await compareAndSwapRecord(stored.key, stored.storageRevision, null))
        throw new Error('This section changed in another tab. Review its latest copy before discarding.');
    }
    branchFor(owner, key).source = null;
    const retained = await decodeRecords(owner);
    if (canUnpin && !retained.some(item => !item.value || item.value.clientReferenceId === clientReferenceId))
      await pinClient(owner, clientReferenceId, false);
    window.dispatchEvent(new CustomEvent('vela:editor-recovery-discarded', { detail: { key, clientReferenceId } }));
    changed();
    return true;
  });
}

// Exchange only a verified, unchanged snapshot belonging to the signed-in
// clinician. The legacy key remains available for records of other owners.
export async function migrateEditorRecoveriesToVault() {
  const identity = getOfflineOwnerIdentity();
  if (!identity || !(await getVaultStatus(identity)).unlocked) return;
  const owner = getOfflineOwnerKey();
  for (const record of await listStoredRecords(owner, 'editor-recovery')) {
    if (record.payload?.version === 2) continue;
    const plaintext = await decryptPayload(record.payload);
    const payload = await encryptPayload(plaintext, { requireVault: true });
    if (JSON.stringify(await decryptPayload(payload)) !== JSON.stringify(plaintext)) throw new Error('Device snapshot verification failed. The original was retained.');
    assertOwner(owner);
    await compareAndSwapRecord(record.key, record.storageRevision, { ...record, payload });
  }
}
window.addEventListener('vela:offline-vault-changed', () => {
  void migrateEditorRecoveriesToVault().then(reconcileEditorRecoveryPins).catch(() => {});
});

window.addEventListener('vela:access-context-changed', () => { void reconcileEditorRecoveryPins().catch(() => {}); });
if (getOfflineOwnerIdentity()) void reconcileEditorRecoveryPins().catch(() => {});

registerPreparedRetentionReconciler('editor-recovery', reconcileEditorRecoveryPins);

export async function reconcileEditorSection({ key, clientReferenceId, base, baseRevision }) {
  const copies = await listEditorRecoveries();
  const removed = [];
  for (const copy of copies.filter(item => item.key === key && !item.unreadable
    && (!clientReferenceId || item.clientReferenceId === clientReferenceId))) {
    if (recoveryIsSuperseded(copy, { base, revision: baseRevision })
      && await removeEditorRecovery(copy.key, copy.operationId, copy.snapshotRevision, copy.recoveryId, copy.storageRevision)) removed.push(copy.recoveryId);
  }
  return removed;
}

export async function reconcileEditorRecoveries(clientReferenceId, resources) {
  const copies = await listEditorRecoveries();
  for (const copy of copies.filter(item => item.clientReferenceId === clientReferenceId && !item.unreadable)) {
    const type = recoveryType(copy.chartType);
    const explicitReference = recoveryRecordId(copy);
    const operationHex = String(copy.state?.clientRequestId || copy.operationId || '').replace(/-/g, '');
    // Older new-visit editors can lack the returned reference. Creation uses
    // the same operation-derived reference; never match merely by patient/date.
    const reference = explicitReference || (/^[a-f0-9]{32}$/i.test(operationHex)
      ? btoa(operationHex.slice(0, 24).match(/../g).map(pair => String.fromCharCode(parseInt(pair, 16))).join('')).replace(/\+/g, '-').replace(/\//g, '_') : null);
    const resource = resources.find(item => recoveryType(item.resourceType) === type
      && reference != null && [item.referenceId, item.base?.id, item.base?.referenceId].some(id => id != null && String(id) === String(reference)));
    if (recoveryHasNoEdits(copy) || (resource?.revision > 0 && recoveryIsSuperseded(copy, resource)))
      await removeEditorRecovery(copy.key, copy.operationId, copy.snapshotRevision, copy.recoveryId, copy.storageRevision);
  }
}
